/* /recruit — hiring pipeline (kanban) with stage moves. */
import "./pay.css";
import { useState } from "react";
import { useCandidates, useMoveCandidate } from "../../data/hooks";
import type { Candidate } from "../../data/types";
import { useI18n } from "../../i18n";
import { AiNote, Button, ErrorBox, Icon, IconButton, PageHeader, Person, Skeleton, Toggle, errMsg, useToast } from "../../ui";

export const STAGES: [string, string][] = [["Applied", "تقدم"], ["Screening", "الفرز"], ["Interview", "المقابلة"], ["Offer", "العرض"], ["Hired", "تم التعيين"]];
export const nextStage = (s: string) => { const i = STAGES.findIndex((x) => x[0] === s); return i >= 0 && i < STAGES.length - 1 ? STAGES[i + 1][0] : null; };

export default function Recruit() {
  const { L, isAr } = useI18n();
  const toast = useToast();
  const q = useCandidates();
  const move = useMoveCandidate();
  const [showRejected, setShowRejected] = useState(false);
  const [busy, setBusy] = useState<number | null>(null);
  if (q.error) return <ErrorBox error={q.error} onRetry={() => q.refetch()} />;
  if (!q.data) return <Skeleton />;
  const stageName = (s: string) => { const x = STAGES.find((y) => y[0] === s); return x ? (isAr ? x[1] : x[0]) : isAr && s === "Rejected" ? "مرفوض" : s; };
  const doMove = (c: Candidate, stage: string) => {
    setBusy(c.id);
    move.mutate({ id: c.id, stage }, {
      onSuccess: () => toast(stage === "Rejected" ? L(c.name + " rejected", "تم رفض " + c.name) : L(c.name + " moved to " + stage, "نُقل " + c.name + " إلى " + stageName(stage)), stage === "Rejected" ? "bad" : "ok"),
      onError: (e) => toast(errMsg(e), "bad"),
      onSettled: () => setBusy(null),
    });
  };
  const cols = showRejected ? [...STAGES, ["Rejected", "مرفوض"] as [string, string]] : STAGES;
  const rejected = q.data.filter((c) => c.stage === "Rejected").length;

  return (
    <>
      <PageHeader title={L("Recruitment", "التوظيف")} sub={L("Post jobs, screen CVs with AI and move candidates through your pipeline.", "انشر الوظائف وافرز السير الذاتية بالذكاء الاصطناعي وانقل المرشحين عبر المراحل.")}
        actions={<>
          <span className="row nw sm" style={{ gap: 8 }}><Toggle checked={showRejected} onChange={setShowRejected} label={L("Show rejected", "إظهار المرفوضين")} testId="toggle-rejected" />{L("Show rejected", "إظهار المرفوضين")} <span className="chip">{rejected}</span></span>
          <Button variant="pri" icon="plus" onClick={() => toast(L("AI drafted the job description — review it in the editor", "صاغ الذكاء وصف الوظيفة — راجعه في المحرر"))} data-testid="btn-new-job">{L("New job", "وظيفة جديدة")}</Button>
        </>} />
      <AiNote>{L("Candidates are scored against each job's must-haves. Scores are suggestions — a person makes every hiring decision.", "يُقيّم المرشحون مقابل متطلبات الوظيفة. التقييم اقتراح والقرار بشري.")}</AiNote>
      <div className="kanban mt" data-testid="pipeline">
        {cols.map(([k]) => {
          const list = q.data.filter((c) => c.stage === k).sort((a, b) => b.score - a.score);
          return (
            <div className="kcol" key={k} data-testid={"col-" + k}>
              <div className="kcol-h"><span>{stageName(k)}</span><span className="cnt num">{list.length}</span></div>
              {list.length === 0 && <div className="xs muted" style={{ padding: 8 }}>{L("No candidates", "لا يوجد مرشحون")}</div>}
              {list.map((c) => {
                const nx = nextStage(c.stage);
                return (
                  <div className="kcard" key={c.id} data-testid={"cand-" + c.id}>
                    <Person e={{ nameEn: c.name, nameAr: c.name, title: c.role }} meta={c.role} />
                    <div className="row between" style={{ marginTop: 10 }}>
                      <span className={"chip " + (c.score >= 80 ? "ok" : c.score >= 70 ? "info" : "")}><Icon name="sparkle" className="s" />{c.score}</span>
                      <span className="row nw">
                        {c.stage !== "Rejected" && c.stage !== "Hired" && <IconButton icon="x" label={L("Reject ", "رفض ") + c.name} onClick={() => doMove(c, "Rejected")} disabled={busy === c.id} data-testid={"reject-" + c.id} />}
                        {c.stage === "Rejected" && <IconButton icon="refresh" label={L("Back to screening", "إعادة للفرز")} onClick={() => doMove(c, "Screening")} disabled={busy === c.id} data-testid={"restore-" + c.id} />}
                        {nx && <IconButton icon="chevron-right" label={L("Move to " + nx, "نقل إلى " + stageName(nx))} onClick={() => doMove(c, nx)} disabled={busy === c.id} data-testid={"next-" + c.id} />}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </>
  );
}
